import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FileText, Download, Search, CheckCircle, Loader2, ExternalLink,
  Award, Shield
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const item = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } } };

const CertificatesPage = () => {
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [verifyId, setVerifyId] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [showVerify, setShowVerify] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => { fetchCompletedEnrollments(); }, []);

  const fetchCompletedEnrollments = async () => {
    setLoading(true);
    try {
      // Fetch users and their enrollments
      const [usersRes, coursesRes] = await Promise.all([
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/courses").catch(() => ({ data: [] })),
      ]);
      const users = usersRes.data || [];
      const courses = coursesRes.data || [];

      // Fetch all enrollments
      const allEnrollments = [];
      for (const u of users.filter(u => u.role === "learner").slice(0, 50)) {
        try {
          const res = await api.get(`/enrollments/user/${u.id}`);
          const completed = (res.data || []).filter(e => e.status === "completed");
          for (const e of completed) {
            const course = courses.find(c => c.id === e.course_id);
            allEnrollments.push({
              ...e,
              learner_name: u.full_name,
              learner_email: u.email,
              course_title: course?.title || "Unknown Course",
            });
          }
        } catch (err) { /* skip */ }
      }
      setEnrollments(allEnrollments);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const downloadCertificate = async (enrollment) => {
    setGenerating(enrollment.id);
    try {
      const token = localStorage.getItem("access_token");
      const response = await fetch(`${API}/certificates/generate/${enrollment.id}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to generate certificate");

      const certId = response.headers.get("X-Certificate-Id") || "";
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate_${certId || enrollment.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Certificate downloaded!", { description: certId ? `Verification ID: ${certId}` : "" });
    } catch (e) {
      toast.error("Failed to generate certificate");
    } finally {
      setGenerating(null);
    }
  };

  const handleVerify = async () => {
    if (!verifyId.trim()) return;
    setVerifying(true);
    try {
      const res = await api.get(`/certificates/verify/${verifyId.trim()}`);
      setVerifyResult(res.data);
    } catch (e) {
      setVerifyResult({ valid: false, message: "Certificate not found" });
    } finally {
      setVerifying(false);
    }
  };

  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  const filtered = enrollments.filter(e =>
    !searchQuery ||
    e.learner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.course_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout title="Certificates" variant={variant}>
      <motion.div className="ent-page space-y-6" variants={container} initial="hidden" animate="show">
        {/* Header */}
        <motion.div variants={item} className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="ent-icon-wrap" style={{ background: "rgba(245,158,11,0.08)" }}>
                <Award className="w-5 h-5 text-amber-500" />
              </div>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900" data-testid="certificates-title">
                Certificates
              </h1>
            </div>
            <p className="text-sm text-slate-500 ml-14">Generate and verify completion certificates</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-xl border-slate-200"
            onClick={() => { setShowVerify(true); setVerifyResult(null); setVerifyId(""); }}>
            <Shield className="w-3.5 h-3.5 mr-1.5" /> Verify Certificate
          </Button>
        </motion.div>

        {/* Stats */}
        <motion.div variants={item} className="grid grid-cols-2 gap-4">
          <div className="ent-stat">
            <div className="flex items-center gap-3">
              <div className="ent-icon-wrap" style={{ background: "rgba(16,185,129,0.08)" }}>
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <div className="stat-num" style={{ fontSize: "1.5rem" }}>{enrollments.length}</div>
                <div className="stat-label">Completed Courses</div>
              </div>
            </div>
          </div>
          <div className="ent-stat">
            <div className="flex items-center gap-3">
              <div className="ent-icon-wrap" style={{ background: "rgba(59,130,246,0.08)" }}>
                <FileText className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <div className="stat-num" style={{ fontSize: "1.5rem" }}>{enrollments.length}</div>
                <div className="stat-label">Certificates Available</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search */}
        <motion.div variants={item}>
          <div className="relative max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search learner or course..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl border-slate-200 bg-white" data-testid="cert-search" />
          </div>
        </motion.div>

        {/* Certificates List */}
        <motion.div variants={item} className="ent-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="ent-empty">
              <div className="ent-empty-icon">
                <Award className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">No completed courses found</h3>
              <p className="text-sm text-slate-500">Certificates become available when learners complete courses</p>
            </div>
          ) : (
            <>
              <div className="flex items-center px-5 py-3 bg-slate-50/80 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                <div className="flex-1">Learner</div>
                <div className="w-64">Course</div>
                <div className="w-20 text-center">Score</div>
                <div className="w-32 text-right">Actions</div>
              </div>
              {filtered.map((e, idx) => (
                <div key={e.id} className="ent-table-row" data-testid={`cert-row-${idx}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{e.learner_name}</p>
                    <p className="text-xs text-slate-400">{e.learner_email}</p>
                  </div>
                  <div className="w-64">
                    <p className="text-sm text-slate-600 truncate">{e.course_title}</p>
                  </div>
                  <div className="w-20 text-center">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                      {e.score || "N/A"}%
                    </span>
                  </div>
                  <div className="w-32 flex justify-end">
                    <Button size="sm" className="ent-btn-primary !py-1 !px-3 !text-xs"
                      onClick={() => downloadCertificate(e)}
                      disabled={generating === e.id}
                      data-testid={`download-cert-${idx}`}>
                      {generating === e.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      ) : (
                        <Download className="w-3.5 h-3.5 mr-1" />
                      )}
                      Download PDF
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </motion.div>
      </motion.div>

      {/* Verify Dialog */}
      <Dialog open={showVerify} onOpenChange={setShowVerify}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-extrabold text-slate-900">
              <Shield className="w-5 h-5 text-blue-500" /> Verify Certificate
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter Certificate ID (e.g., CERT-XXXX-XXXX)"
                value={verifyId}
                onChange={e => setVerifyId(e.target.value)}
                className="rounded-xl border-slate-200"
                data-testid="verify-cert-input"
              />
              <Button className="ent-btn-primary !px-4" onClick={handleVerify} disabled={verifying}>
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
              </Button>
            </div>
            {verifyResult && (
              <div className={`p-4 rounded-xl border-2 ${
                verifyResult.valid
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {verifyResult.valid ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <ExternalLink className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`font-bold ${verifyResult.valid ? "text-emerald-700" : "text-red-700"}`}>
                    {verifyResult.valid ? "Valid Certificate" : "Invalid Certificate"}
                  </span>
                </div>
                {verifyResult.valid && (
                  <div className="space-y-1 text-sm text-slate-600">
                    <p><strong>Learner:</strong> {verifyResult.learner_name}</p>
                    <p><strong>Course:</strong> {verifyResult.course_title}</p>
                    {verifyResult.score && <p><strong>Score:</strong> {verifyResult.score}%</p>}
                    {verifyResult.completed_at && <p><strong>Completed:</strong> {new Date(verifyResult.completed_at).toLocaleDateString()}</p>}
                  </div>
                )}
                {!verifyResult.valid && (
                  <p className="text-sm text-red-600">{verifyResult.message || "This certificate ID was not found in our system."}</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CertificatesPage;
